import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ScanLine, Loader2, CheckCircle2, Circle, Package, RefreshCw, PartyPopper, Boxes, Download, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import * as api from '@/services/seasonalWmsApi';

const SeasonalMobilePicking = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState(null);      // {pick_slip, boxes, picked_count, total_count}
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [closing, setClosing] = useState(false);
  const inputRef = useRef(null);

  const openSlip = useCallback(async (id) => {
    const d = await api.getSlipDetail(id);
    setActive(d); setCode('');
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  // Land the assigned picker DIRECTLY on their pick slip — no slip list.
  const bootstrap = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getMySlips();
      const list = r.slips || [];
      if (list.length) await openSlip(list[0].pick_slip_id);
      else setActive(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load your pick slip');
      setActive(null);
    } finally { setLoading(false); }
  }, [openSlip]);
  useEffect(() => { bootstrap(); }, [bootstrap]);

  const refreshActive = useCallback(async () => {
    if (!active) { await bootstrap(); return; }
    try { const d = await api.getSlipDetail(active.pick_slip.pick_slip_id); setActive(d); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to refresh'); }
  }, [active, bootstrap]);

  const scan = async () => {
    const c = code.trim();
    if (!c || !active) return;
    setScanning(true);
    try {
      const res = await api.scanPick(active.pick_slip.pick_slip_id, c);
      toast.success(res.message);
      setCode('');
      const d = await api.getSlipDetail(active.pick_slip.pick_slip_id);
      setActive(d);
      inputRef.current?.focus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
      setCode(''); inputRef.current?.focus();
    } finally { setScanning(false); }
  };

  const downloadMasterSlip = async (mcId, mcNo) => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(api.masterCartonSlipUrl(mcId), { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `master_carton_${String(mcNo || mcId).replace(/\//g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download master slip'); }
  };

  const closeCurrentCarton = async () => {
    if (!active) return;
    setClosing(true);
    try {
      const res = await api.closeCarton(active.pick_slip.pick_slip_id);
      toast.success(res.message);
      await downloadMasterSlip(res.mc_id, res.master_carton_no);
      const d = await api.getSlipDetail(active.pick_slip.pick_slip_id);
      setActive(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to close carton');
    } finally { setClosing(false); }
  };

  const Header = ({ subtitle }) => (
    <div className="bg-[#1A1F2E] px-4 h-16 flex items-center gap-3 sticky top-0 z-10">
      <Button variant="ghost" size="sm" onClick={() => navigate('/wms/seasonal-bundle')} className="text-white hover:bg-white/10" data-testid="smp-back-btn">
        <ArrowLeft className="w-4 h-4" />
      </Button>
      <ScanLine className="w-6 h-6 text-amber-400" />
      <div className="flex-1"><h1 className="text-lg font-bold">Packed Bundle Picking</h1><p className="text-xs text-[#E8DCC8]">{subtitle}</p></div>
      <Button variant="ghost" size="sm" onClick={refreshActive} className="text-white hover:bg-white/10" data-testid="smp-refresh-btn">
        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
      </Button>
    </div>
  );

  // ---------- Loading ----------
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F1420] text-white" data-testid="seasonal-picking-page">
        <Header subtitle="Loading your pick slip…" />
        <p className="text-center text-gray-400 py-16"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</p>
      </div>
    );
  }

  // ---------- No assigned slip ----------
  if (!active) {
    return (
      <div className="min-h-screen bg-[#0F1420] text-white" data-testid="seasonal-picking-page">
        <Header subtitle="No pick slip assigned" />
        <div className="max-w-md mx-auto p-4 text-center">
          <p className="text-gray-400 py-16" data-testid="smp-no-slips">No pick slip assigned to you.</p>
          <Button onClick={bootstrap} className="bg-amber-600 hover:bg-amber-700" data-testid="smp-recheck-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Check again
          </Button>
        </div>
      </div>
    );
  }

  // ---------- Active slip picking (direct) ----------
  const { pick_slip: slip, boxes, picked_count, total_count, open_carton, closed_cartons } = active;
  const done = picked_count >= total_count;

  // Guide the picker LOCATION-WISE: clear every bundle in one bin before the next.
  // Unpicked bundles are grouped by bin and shown at the top (current bin highlighted);
  // picked bundles drop into a greyed section at the bottom.
  const unpicked = (boxes || []).filter((b) => !b.picked);
  const pickedBoxes = (boxes || []).filter((b) => b.picked);
  const groupMap = new Map();
  unpicked.forEach((b) => {
    const key = b.footprint || 'No bin assigned';
    if (!groupMap.has(key)) groupMap.set(key, []);
    groupMap.get(key).push(b);
  });
  const locationGroups = [...groupMap.entries()]
    .map(([loc, items]) => ({ loc, items }))
    .sort((a, b) => a.loc.localeCompare(b.loc, undefined, { numeric: true }));
  const currentLoc = locationGroups[0]?.loc;
  const currentCount = locationGroups[0]?.items.length || 0;

  const renderBox = (b) => (
    <div key={b.box_id} className={`flex items-center gap-3 rounded-lg px-3 py-2.5 border ${b.picked ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-[#1A1F2E] border-white/10'}`} data-testid={`smp-box-${b.box_number}`}>
      {b.picked ? <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" /> : <Circle className="w-5 h-5 text-gray-500 shrink-0" />}
      <Package className="w-4 h-4 text-gray-400 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className={`font-mono text-sm ${b.picked ? 'text-emerald-300 line-through' : 'text-white'}`} data-testid={`smp-box-display-${b.box_id}`}>{b.display_no || b.box_number}</p>
        {b.external_box_no && b.external_box_no !== b.box_number && (
          <p className="text-[11px] text-gray-500 font-mono" data-testid={`smp-box-sp-${b.box_id}`}>SP: {b.box_number}</p>
        )}
        <p className="text-xs text-gray-500">{b.footprint || 'no bin'} · {b.total_qty} pcs</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0F1420] text-white" data-testid="seasonal-picking-active">
      <div className="bg-[#1A1F2E] px-4 h-16 flex items-center gap-3 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate('/wms/seasonal-bundle')} className="text-white hover:bg-white/10" data-testid="smp-active-back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-base font-bold truncate" data-testid="smp-active-customer">{slip.customer}</h1>
          <p className="text-xs text-[#E8DCC8]">DC {slip.dc_number} · {picked_count}/{total_count} picked</p>
        </div>
        <Button variant="ghost" size="sm" onClick={refreshActive} className="text-white hover:bg-white/10" data-testid="smp-refresh-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      <div className="max-w-md mx-auto p-4 space-y-4">
        {done ? (
          <div className="bg-emerald-500/15 border border-emerald-500/40 rounded-xl p-5 text-center" data-testid="smp-complete">
            <PartyPopper className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
            <p className="font-semibold text-emerald-300">All boxes picked!</p>
            <p className="text-xs text-emerald-400/80 mt-1">Slip completed — ready for customer outward.</p>
            <Button onClick={bootstrap} className="mt-4 bg-emerald-600 hover:bg-emerald-700" data-testid="smp-done-btn">Pick next slip</Button>
          </div>
        ) : (
          <div className="bg-[#1A1F2E] rounded-xl border border-white/10 p-4">
            <label className="text-xs text-gray-400">Scan small-box code to pick (SP box code also works)</label>
            <div className="flex gap-2 mt-1">
              <Input ref={inputRef} autoFocus value={code} onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') scan(); }}
                placeholder="Scan box…" className="flex-1 font-mono bg-[#0F1420] border-white/20 text-white" data-testid="smp-scan-input" />
              <Button onClick={scan} disabled={scanning} className="bg-amber-600 hover:bg-amber-700" data-testid="smp-scan-btn">
                {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
              </Button>
            </div>
            {currentLoc && (
              <p className="text-[11px] text-amber-300/90 mt-2 flex items-center gap-1" data-testid="smp-current-loc-hint">
                <MapPin className="w-3.5 h-3.5 shrink-0" /> Go to <b className="font-mono">{currentLoc}</b> and pick all <b>{currentCount}</b> bundle(s) there first
              </p>
            )}
          </div>
        )}

        {/* Master carton — current + closed */}
        {open_carton && open_carton.total_boxes > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-4" data-testid="smp-open-carton">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-300/80">Current Master Carton</p>
                <p className="font-mono font-semibold text-amber-200" data-testid="smp-open-carton-no">{open_carton.master_carton_no}</p>
              </div>
              <Badge className="bg-amber-500/20 text-amber-200 border-0">{open_carton.total_boxes} boxes</Badge>
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {(open_carton.box_display_numbers || []).map((n) => (
                <span key={n} className="text-xs font-mono bg-black/30 rounded px-1.5 py-0.5 text-amber-100">{n}</span>
              ))}
            </div>
            <Button onClick={closeCurrentCarton} disabled={closing} className="mt-3 w-full bg-amber-600 hover:bg-amber-700" data-testid="smp-close-carton-btn">
              {closing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Boxes className="w-4 h-4 mr-2" />}
              Close carton & print master slip
            </Button>
          </div>
        )}

        {(closed_cartons || []).length > 0 && (
          <div className="bg-[#1A1F2E] border border-white/10 rounded-xl p-4" data-testid="smp-closed-cartons">
            <p className="text-xs text-gray-400 mb-2">Closed master cartons</p>
            <div className="space-y-2">
              {closed_cartons.map((mc) => (
                <div key={mc.mc_id} className="flex items-center justify-between gap-2 rounded-lg bg-black/20 px-3 py-2" data-testid={`smp-closed-carton-${mc.mc_id}`}>
                  <div className="min-w-0">
                    <p className="font-mono text-sm text-emerald-300 truncate">{mc.master_carton_no}</p>
                    <p className="text-xs text-gray-500">{mc.total_boxes} boxes · {mc.total_qty} pcs</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => downloadMasterSlip(mc.mc_id, mc.master_carton_no)} className="border-white/20 text-white hover:bg-white/10" data-testid={`smp-master-slip-${mc.mc_id}`}>
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-3" data-testid="smp-box-list">
          {locationGroups.map((g, gi) => (
            <div key={g.loc} data-testid={`smp-loc-group-${gi}`}>
              <div className={`flex items-center justify-between rounded-lg px-3 py-2 mb-2 ${gi === 0 ? 'bg-amber-500/20 border border-amber-500/50' : 'bg-white/5 border border-white/10'}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <MapPin className={`w-4 h-4 shrink-0 ${gi === 0 ? 'text-amber-300' : 'text-gray-400'}`} />
                  <div className="min-w-0">
                    <p className={`text-[10px] uppercase tracking-wide ${gi === 0 ? 'text-amber-300 font-semibold' : 'text-gray-500'}`}>
                      {gi === 0 ? 'Pick here now' : 'Next location'}
                    </p>
                    <p className={`font-mono text-sm truncate ${gi === 0 ? 'text-amber-100' : 'text-gray-300'}`} data-testid={`smp-loc-name-${gi}`}>{g.loc}</p>
                  </div>
                </div>
                <Badge className={`border-0 shrink-0 ${gi === 0 ? 'bg-amber-500/30 text-amber-100' : 'bg-white/10 text-gray-300'}`} data-testid={`smp-loc-count-${gi}`}>
                  {g.items.length} left
                </Badge>
              </div>
              <div className="space-y-2">{g.items.map(renderBox)}</div>
            </div>
          ))}

          {pickedBoxes.length > 0 && (
            <div data-testid="smp-picked-group">
              <div className="flex items-center gap-2 rounded-lg px-3 py-2 mb-2 bg-emerald-500/10 border border-emerald-500/20">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <p className="text-xs text-emerald-300" data-testid="smp-picked-count">Picked ({pickedBoxes.length})</p>
              </div>
              <div className="space-y-2 opacity-60">{pickedBoxes.map(renderBox)}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SeasonalMobilePicking;
